import { useState } from 'react';
import {
  Sparkles,
  Settings,
  TrendingUp,
  Clock,
  ExternalLink,
  Info,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';

// Fantasy platforms
const fantasyPlatforms = [
  {
    id: 'draftkings',
    name: 'DraftKings',
    logo: 'DK',
    status: 'connected',
    lastSync: '2024-01-15T10:30:00',
    players: 12450,
    contests: 24,
  },
  {
    id: 'fanduel',
    name: 'FanDuel',
    logo: 'FD',
    status: 'connected',
    lastSync: '2024-01-15T09:15:00',
    players: 8920,
    contests: 18,
  },
  {
    id: 'yahoo',
    name: 'Yahoo Fantasy',
    logo: 'Y!',
    status: 'disconnected',
    lastSync: null,
    players: 0,
    contests: 0,
  },
];

// Scoring categories
const scoringCategories = [
  { id: 'finish', name: 'Finish Position', points: 'Variable', enabled: true },
  { id: 'position_diff', name: 'Position Differential', points: '+/- 0.5/pos', enabled: true },
  { id: 'laps_led', name: 'Laps Led', points: '0.25/lap', enabled: true },
  { id: 'fastest_lap', name: 'Fastest Lap', points: '5', enabled: true },
  { id: 'top_5', name: 'Top 5 Finish', points: '10', enabled: true },
  { id: 'win', name: 'Win Bonus', points: '25', enabled: true },
  { id: 'dnf', name: 'DNF Penalty', points: '-10', enabled: false },
];

function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        enabled ? 'bg-rms-accent' : 'bg-slate-300'
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function PlatformCard({ platform }) {
  const isConnected = platform.status === 'connected';

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
            isConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {platform.logo}
          </div>
          <div>
            <div className="font-semibold text-slate-800">{platform.name}</div>
            <div className="text-xs text-slate-500">
              {isConnected ? `Synced ${formatDate(platform.lastSync)}` : 'Not connected'}
            </div>
          </div>
        </div>
        <Badge variant={isConnected ? 'success' : 'neutral'}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </Badge>
      </div>
      {isConnected && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-2 bg-slate-50 rounded">
            <div className="text-lg font-bold text-slate-800">{platform.players.toLocaleString()}</div>
            <div className="text-xs text-slate-500">Players</div>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded">
            <div className="text-lg font-bold text-slate-800">{platform.contests}</div>
            <div className="text-xs text-slate-500">Active Contests</div>
          </div>
        </div>
      )}
      <Button variant={isConnected ? 'secondary' : 'primary'} size="sm" className="w-full">
        {isConnected ? 'Manage Connection' : 'Connect Platform'}
      </Button>
    </div>
  );
}

export default function FantasyConfig() {
  const [fantasyEnabled, setFantasyEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [scoring, setScoring] = useState(scoringCategories);

  const toggleScoring = (id) => {
    setScoring(prev => prev.map(cat =>
      cat.id === id ? { ...cat, enabled: !cat.enabled } : cat
    ));
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="page-title">Fantasy Integration</h1>
            <p className="page-subtitle">Configure fantasy sports platform connections</p>
          </div>
        </div>
        <Button variant="primary" leftIcon={Settings}>
          Advanced Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Master Toggle */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    fantasyEnabled ? 'bg-purple-100' : 'bg-slate-100'
                  }`}>
                    <Sparkles className={`w-6 h-6 ${fantasyEnabled ? 'text-purple-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Fantasy Integration</h3>
                    <p className="text-sm text-slate-500">Enable fantasy sports data sharing</p>
                  </div>
                </div>
                <Toggle enabled={fantasyEnabled} onChange={setFantasyEnabled} />
              </div>
            </CardContent>
          </Card>

          {/* Connected Platforms */}
          <Card>
            <CardHeader>
              <CardTitle>Connected Platforms</CardTitle>
              <CardDescription>Manage fantasy sports platform integrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {fantasyPlatforms.map(platform => (
                  <PlatformCard key={platform.id} platform={platform} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Scoring Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Scoring Configuration</CardTitle>
              <CardDescription>Define how fantasy points are calculated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {scoring.map(category => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <Toggle
                        enabled={category.enabled}
                        onChange={() => toggleScoring(category.id)}
                      />
                      <div>
                        <div className="font-medium text-slate-700">{category.name}</div>
                        <div className="text-xs text-slate-500">Points: {category.points}</div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">Edit</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle>Data Sharing</CardTitle>
              <CardDescription>Control what data is shared with fantasy platforms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-medium text-slate-700">Driver Statistics</div>
                  <div className="text-sm text-slate-500">Season stats, averages, and history</div>
                </div>
                <Toggle enabled={true} onChange={() => {}} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-medium text-slate-700">Live Timing Data</div>
                  <div className="text-sm text-slate-500">Real-time lap times and positions</div>
                </div>
                <Toggle enabled={true} onChange={() => {}} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-medium text-slate-700">Qualifying Results</div>
                  <div className="text-sm text-slate-500">Time trial and qualifying data</div>
                </div>
                <Toggle enabled={true} onChange={() => {}} />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-medium text-slate-700">Practice Data</div>
                  <div className="text-sm text-slate-500">Practice session times and notes</div>
                </div>
                <Toggle enabled={false} onChange={() => {}} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                Fantasy Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Total Players</span>
                <span className="font-bold text-slate-800">21,370</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Active Contests</span>
                <span className="font-bold text-slate-800">42</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Events This Season</span>
                <span className="font-bold text-slate-800">38</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Total Prize Pool</span>
                <span className="font-bold text-emerald-600">$2.4M</span>
              </div>
            </CardContent>
          </Card>

          {/* Sync Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Sync Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-700 text-sm">Auto-Sync</div>
                  <div className="text-xs text-slate-500">Sync data automatically</div>
                </div>
                <Toggle enabled={autoSync} onChange={setAutoSync} />
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">Sync Frequency</div>
                <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  <option>Every 5 minutes</option>
                  <option>Every 15 minutes</option>
                  <option>Every 30 minutes</option>
                  <option>Every hour</option>
                </select>
              </div>
              <Button variant="secondary" size="sm" className="w-full">
                Sync Now
              </Button>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Recent Syncs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <div className="flex-1">
                    <div className="text-slate-700">DraftKings sync</div>
                    <div className="text-xs text-slate-400">10 min ago</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <div className="flex-1">
                    <div className="text-slate-700">FanDuel sync</div>
                    <div className="text-xs text-slate-400">25 min ago</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <div className="flex-1">
                    <div className="text-slate-700">Yahoo connection failed</div>
                    <div className="text-xs text-slate-400">1 hour ago</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-800 mb-1">Need Help?</h4>
                  <p className="text-sm text-slate-500 mb-3">
                    Learn how to set up fantasy integrations.
                  </p>
                  <Button variant="secondary" size="sm" rightIcon={ExternalLink}>
                    View Guide
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
